import merge from 'lodash/merge.js';

import axios from '@/plugins/axios.js';
import ElearningPackage from '@/store/modules/elearningpackages/ElearningPackage.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';

const collectionHelpers = EdverumTable.collection('elearning_package');

export default merge(Collection('ElearningPackages', ElearningPackage), {
    state: {
        sort: {
            title: null,
            standard: null,
            updated_at: null,
        },
    },

    getters: {
        ...collectionHelpers.getters,
    },

    actions: {
        clearSort: collectionHelpers.actions.clearSort,

        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters, rootGetters } = context;

            queryBuilder.sort(getters.queryBuilderSorting).setPagination(getters.paginate);

            if (rootGetters['ElearningPackagesView/hasQuery']) {
                queryBuilder.raw({
                    search: rootGetters['ElearningPackagesView/searchQuery'],
                });
            }

            if (rootGetters['ElearningPackagesView/hasStandardFilter']) {
                queryBuilder.raw({
                    standard: rootGetters['ElearningPackagesView/standardFilter'],
                });
            }
        }),

        fetchOne: collectionHelpers.actions.fetchOne(({ queryBuilder }) => {
            queryBuilder.include(['latestVersion', 'latestVersion.items', 'versions', 'versions.items']);
        }),

        async uploadVersion(context, { packageId, file, versionLabel, releaseNotes }) {
            const formData = new FormData();
            formData.append('package', file);
            formData.append('version_label', versionLabel);

            if (releaseNotes) {
                formData.append('release_notes', releaseNotes);
            }

            const { data: uploadData } = await axios.post(`/api/elearning-packages/${packageId}/versions`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const versionId = uploadData.data.id;

            await axios.post(`/api/elearning-package-versions/${versionId}/process`);

            await context.dispatch('pollVersionUntilReady', { versionId, packageId });

            return uploadData.data;
        },

        async pollVersionUntilReady(context, { versionId, packageId, maxAttempts = 120 }) {
            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                const { data } = await axios.get(`/api/elearning-package-versions/${versionId}`);
                const version = data.data;

                if (version.status === 'ready') {
                    await context.dispatch('fetchOne', packageId);

                    return version;
                }

                if (version.status === 'draft' && version.validation_errors?.length) {
                    const message = version.validation_errors.map(error => error.message).join(' ');
                    throw new Error(message || 'Package validation failed.');
                }

                await new Promise(resolve => setTimeout(resolve, 500));
            }

            throw new Error('Package processing timed out.');
        },

        async createPackage(context, payload) {
            const { data } = await axios.post('/api/elearning-packages', payload);

            await context.dispatch('add', data.data);

            return data.data;
        },

        removeChecked: collectionHelpers.actions.removeChecked(),
    },
});
